import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getApiBaseUrl } from '../../utils/apiConfig';
import './ConsumeForm.css';

/**
 * 消费表单组件
 * 
 * @param {Object} props
 * @param {Function} props.onClose - 关闭回调
 * @param {Function} props.onSuccess - 成功回调
 */
const ConsumeForm = ({ courtId, onClose, onSuccess }) => {
  const [courts, setCourts] = useState([]);
  const [subAccounts, setSubAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    courtId: courtId || '',
    operationDate: new Date().toISOString().split('T')[0],
    courtTime: '',
    participants: '',
    description: '',
    subAccountId: ''
  });

  const baseUrl = getApiBaseUrl();

  // 获取场地列表
  useEffect(() => {
    const fetchCourts = async () => {
      try {
        const response = await axios.get(`${baseUrl}/api/court-usage/courts?isActive=true`);
        if (response.data.success) {
          setCourts(response.data.data || []);
          if (courtId) {
            setFormData(prev => ({ ...prev, courtId }));
          } else if (response.data.data && response.data.data.length > 0) {
            setFormData(prev => ({ ...prev, courtId: response.data.data[0]._id }));
          }
        }
      } catch (err) {
        console.error('获取场地列表失败:', err);
      }
    };
    fetchCourts();
  }, [courtId]);

  // 获取子账户列表（按场地）
  useEffect(() => {
    const fetchSubAccounts = async () => {
      if (!formData.courtId) {
        setSubAccounts([]);
        return;
      }
      
      try {
        const response = await axios.get(`${baseUrl}/api/court-usage/sub-accounts`, {
          params: { courtId: formData.courtId, isActive: true }
        });
        if (response.data.success) {
          setSubAccounts(response.data.data || []);
        }
      } catch (err) {
        console.error('获取子账户列表失败:', err);
      }
    };
    fetchSubAccounts();
  }, [formData.courtId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.courtId) {
      alert('请选择场地');
      return;
    }
    
    if (!formData.courtTime || parseFloat(formData.courtTime) <= 0) {
      alert('请输入有效的消费场时数');
      return;
    }

    try {
      setLoading(true);
      const participants = formData.participants
        ? formData.participants.split(/[,，]/).map(p => p.trim()).filter(p => p)
        : [];

      const requestData = {
        courtId: formData.courtId,
        operationDate: formData.operationDate,
        courtTime: parseFloat(formData.courtTime),
        participants,
        description: formData.description
      };
      
      // 如果选择了子账户，添加关联信息（会同时扣除主账户和子账户）
      if (formData.subAccountId) {
        requestData.subAccountId = formData.subAccountId;
      }

      const response = await axios.post(`${baseUrl}/api/court-usage/main-account/consume`, requestData);

      if (response.data.success) {
        onSuccess();
      } else {
        throw new Error(response.data.message || '消费失败');
      }
    } catch (err) {
      console.error('消费失败:', err);
      alert(err.response?.data?.message || err.message || '消费失败');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content form-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>添加消费记录</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        
        <form onSubmit={handleSubmit} className="consume-form">
          {!courtId && (
            <div className="form-group">
              <label>场地 *</label>
              <select
                value={formData.courtId}
                onChange={(e) => setFormData({ ...formData, courtId: e.target.value })}
                required
              >
                <option value="">请选择场地</option>
                {courts.map(court => (
                  <option key={court._id} value={court._id}>
                    {court.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="form-group">
            <label>使用日期 *</label>
            <input
              type="date"
              value={formData.operationDate}
              onChange={(e) => setFormData({ ...formData, operationDate: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label>消费场时数 *</label>
            <input
              type="number"
              step="0.5"
              min="0.5"
              value={formData.courtTime}
              onChange={(e) => setFormData({ ...formData, courtTime: e.target.value })}
              placeholder="请输入消费的场时数"
              required
            />
          </div>

          <div className="form-group">
            <label>关联子账户（可选）</label>
            <select
              value={formData.subAccountId}
              onChange={(e) => setFormData({ ...formData, subAccountId: e.target.value })}
            >
              <option value="">不关联子账户</option>
              {subAccounts.map(account => (
                <option key={account._id} value={account._id}>
                  {account.name} (余额: {account.currentBalance.toFixed(2)} 场时)
                </option>
              ))}
            </select>
            {formData.subAccountId && (
              <div className="form-hint" style={{ marginTop: '8px', color: '#666', fontSize: '13px' }}>
                选择子账户后，消费将同时从主账户和子账户扣除
              </div>
            )}
          </div>

          <div className="form-group">
            <label>参与人员</label>
            <input
              type="text"
              value={formData.participants}
              onChange={(e) => setFormData({ ...formData, participants: e.target.value })}
              placeholder="多个人员用逗号分隔，如：张三,李四"
            />
          </div>

          <div className="form-group">
            <label>说明</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="请输入说明（可选）"
              rows="3"
            />
          </div>

          <div className="form-actions">
            <button type="button" className="cancel-button" onClick={onClose}>
              取消
            </button>
            <button type="submit" className="submit-button" disabled={loading}>
              {loading ? '提交中...' : '提交'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ConsumeForm;

